import React from 'react';
import { motion } from 'framer-motion';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ProcessSection = () => {
  const steps = [
    {
      id: 1,
      icon: 'Phone',
      title: 'Anrufen',
      description: 'Wählen Sie unsere Hotline und werden Sie sofort mit einem qualifizierten Anwalt verbunden.',
      details: ['Keine Wartezeiten', 'Direkter Kontakt', '24/7 verfügbar'],
      color: 'from-primary to-brand-primary'
    },
    {
      id: 2,
      icon: 'MessageCircle',
      title: 'Schildern',
      description: 'Erklären Sie Ihr rechtliches Problem. Unser Anwalt hört aufmerksam zu und stellt gezielte Fragen.',
      details: ['Vertraulich', 'Strukturiert', 'Verständlich'],
      color: 'from-conversion-accent to-cta'
    },
    {
      id: 3,
      icon: 'Lightbulb',
      title: 'Beraten lassen',
      description: 'Erhalten Sie eine fundierte rechtliche Einschätzung und konkrete Handlungsempfehlungen.',
      details: ['Professionell', 'Verständlich', 'Umsetzbar'],
      color: 'from-trust-builder to-success'
    },
    {
      id: 4,
      icon: 'CheckCircle',
      title: 'Handeln',
      description: 'Setzen Sie die erhaltenen Empfehlungen um oder lassen Sie sich bei weiteren Schritten begleiten.',
      details: ['Klar definiert', 'Nachverfolgbar', 'Erfolgreich'],
      color: 'from-accent to-primary'
    }
  ];

  return (
    <section className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="space-y-4"
          >
            <h2 className="text-3xl sm:text-4xl font-bold text-text-primary">
              So einfach funktioniert's
            </h2>
            <p className="text-lg text-text-secondary max-w-2xl mx-auto">
              In nur vier einfachen Schritten zu Ihrer rechtlichen Lösung. 
              Transparent, schnell und professionell.
            </p>
          </motion.div>
        </div>

        {/* Process Steps */}
        <div className="relative">
          {/* Connection Line */}
          <div className="hidden lg:block absolute top-24 left-1/2 transform -translate-x-1/2 w-full max-w-4xl">
            <div className="relative h-1 bg-gradient-to-r from-primary via-conversion-accent via-trust-builder to-accent rounded-full opacity-20"></div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {steps?.map((step, index) => (
              <motion.div
                key={step?.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="relative group"
              >
                {/* Step Card */}
                <div className="bg-white rounded-xl shadow-professional hover:shadow-elevated transition-all duration-300 card-hover p-8 text-center relative z-10">
                  {/* Step Number */}
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <div className={`w-8 h-8 rounded-full bg-gradient-to-r ${step?.color} flex items-center justify-center text-white font-bold text-sm`}>
                      {step?.id}
                    </div>
                  </div>

                  {/* Icon */}
                  <div className={`inline-flex items-center justify-center w-20 h-20 rounded-full bg-gradient-to-br ${step?.color} mb-6 mt-4`}>
                    <Icon name={step?.icon} size={40} className="text-white" />
                  </div>

                  {/* Content */}
                  <div className="space-y-4">
                    <h3 className="text-xl font-semibold text-text-primary">
                      {step?.title}
                    </h3>
                    
                    <p className="text-text-secondary leading-relaxed">
                      {step?.description}
                    </p>

                    {/* Details */}
                    <ul className="space-y-2">
                      {step?.details?.map((detail, idx) => (
                        <li key={idx} className="flex items-center justify-center space-x-2 text-sm">
                          <Icon name="Check" size={14} className="text-trust-builder flex-shrink-0" />
                          <span className="text-text-secondary">{detail}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>

                {/* Arrow for larger screens */}
                {index < steps?.length - 1 && (
                  <div className="hidden lg:block absolute top-1/2 -right-4 transform -translate-y-1/2 z-20">
                    <Icon name="ArrowRight" size={24} className="text-slate-300" />
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        </div>

        {/* Pricing Information */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.5 }}
          viewport={{ once: true }}
          className="mt-16 bg-gradient-to-r from-surface to-muted rounded-2xl p-8 sm:p-12"
        >
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
            <div className="space-y-6">
              <h3 className="text-2xl sm:text-3xl font-bold text-text-primary">
                Transparente Preise
              </h3>
              <p className="text-text-secondary leading-relaxed">
                Keine versteckten Kosten, keine Überraschungen. Sie zahlen nur für die Zeit, 
                die Sie tatsächlich beraten werden. Minutengenau abgerechnet.
              </p>
              
              <div className="space-y-4">
                <div className="flex items-center space-x-3">
                  <Icon name="Clock" size={20} className="text-conversion-accent" />
                  <span className="text-text-secondary">Minutengenaue Abrechnung</span>
                </div>
                <div className="flex items-center space-x-3">
                  <Icon name="Shield" size={20} className="text-trust-builder" />
                  <span className="text-text-secondary">Keine Mindestgesprächsdauer</span>
                </div>
                <div className="flex items-center space-x-3">
                  <Icon name="CreditCard" size={20} className="text-primary" />
                  <span className="text-text-secondary">Einfache Bezahlung</span>
                </div>
              </div>
            </div>

            <div className="text-center lg:text-right">
              <div className="inline-block bg-white rounded-2xl p-8 shadow-professional">
                <div className="space-y-4">
                  <div className="text-4xl sm:text-5xl font-bold text-conversion-accent">
                    2,99 €
                  </div>
                  <div className="text-text-secondary">
                    pro Minute
                  </div>
                  <div className="text-sm text-text-secondary">
                    zzgl. Verbindungskosten Ihres Anbieters
                  </div>
                  
                  <Button
                    variant="default"
                    size="lg"
                    iconName="Phone"
                    iconPosition="left"
                    className="bg-conversion-accent hover:bg-cta text-white cta-button w-full"
                  >
                    Jetzt anrufen
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default ProcessSection;